import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useRouter } from 'next/router';
import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { employmentSpecialRelationshipsPastToCurrentlyQuery } from '@/__generated__/employmentSpecialRelationshipsPastToCurrentlyQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CTA, CheckListGroup, CheckList } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { birthToYYMD } from '@/components/survey/common/utils';
import { SpecialRelationshipTitleIndicator } from '@/components/survey/employment/special-relationships/SpecialRelationshipTitleIndicator';
import { updateSpecialRelationshipsPastToCurrentlyQuery } from '@/graphql/mutation/survey/employment';
import { specialRelationshipsPastToCurrentlyQuery } from '@/graphql/query/survey/employment';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertEmployment } from '@/lib/hooks/api/mutation/survey/use-upsert-employment';

export const PastToCurrentlySpecialRelationshipsContainer = ({
  queryRef
}: {
  queryRef: PreloadedQuery<employmentSpecialRelationshipsPastToCurrentlyQuery>;
}) => {
  const { getSurveyEmploymentAnswer } = usePreloadedQuery<employmentSpecialRelationshipsPastToCurrentlyQuery>(
    specialRelationshipsPastToCurrentlyQuery,
    queryRef
  );
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const [upsertEmploymentAnswer, isLoading] = useUpsertEmployment(updateSpecialRelationshipsPastToCurrentlyQuery);

  const [selectedEmployeesSet, setSelectedEmployeesSet] = useState(new Set());
  const pastEmployees = useMemo(
    () =>
      getSurveyEmploymentAnswer.employmentAnswer?.specialRelationshipsInThePast?.map(({ ieNm, birthday }) => ({
        nameBirthdayKey: `${ieNm}${birthday}`,
        name: ieNm,
        birthday: birthday ?? ''
      })) || [],
    [getSurveyEmploymentAnswer.employmentAnswer]
  );
  const currentEmployees = useMemo(
    () => getSurveyEmploymentAnswer.employmentAnswer?.specialRelationshipsCurrently || [],
    [getSurveyEmploymentAnswer.employmentAnswer]
  );

  const toggleEmployee = (nameBirthdayKey: string) => {
    if (selectedEmployeesSet.has(nameBirthdayKey)) {
      selectedEmployeesSet.delete(nameBirthdayKey);
    } else {
      selectedEmployeesSet.add(nameBirthdayKey);
    }
    setSelectedEmployeesSet(new Set(selectedEmployeesSet));
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    sendClickEvent('survey_special-relationships-past_confirm', eventAttributes);

    upsertEmploymentAnswer({
      variables: {
        specialRelationshipsCurrently: pastEmployees
          .filter(({ nameBirthdayKey }) => selectedEmployeesSet.has(nameBirthdayKey))
          .map(({ name, birthday }) => ({ ieNm: name, birthday: birthday || undefined }))
      },
      onCompleted: () => {
        router.push(SURVEY_PAGES.대표특관자_현재);
      }
    });
  };

  useEffect(() => {
    if (currentEmployees.length > 0) {
      const tempKeys = currentEmployees.map(({ ieNm, birthday }) => `${ieNm}${birthday}`);
      setSelectedEmployeesSet(new Set(tempKeys));
    }
  }, [currentEmployees]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_special-relationships-past_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form onSubmit={handleSubmit}>
          <article>
            <SpecialRelationshipTitleIndicator />
            <h2 className={common.content_title}>이 중 현재도 근무하는 4대보험 가입된 직원이 있다면 모두 선택해주세요</h2>
            <CheckListGroup>
              {pastEmployees.map(({ nameBirthdayKey, birthday, name }) => {
                return (
                  <CheckList
                    key={nameBirthdayKey}
                    value={nameBirthdayKey}
                    checked={selectedEmployeesSet.has(nameBirthdayKey)}
                    onChange={() => {
                      sendClickEvent('survey_special-relationships-past_select-employee');
                      toggleEmployee(nameBirthdayKey);
                    }}
                  >
                    {name} ({birthToYYMD(birthday)})
                  </CheckList>
                );
              })}
            </CheckListGroup>
          </article>

          <CTA type={'submit'} disabled={isLoading}>
            {selectedEmployeesSet.size > 0 ? `${selectedEmployeesSet.size}명 선택 완료` : '아무도 근무하고 있지 않아요'}
          </CTA>
        </form>
      </main>
    </>
  );
};
